import { useState } from "react";
import { Briefcase, Handshake, Upload, ArrowRight, Loader2, CheckCircle2, Users, TrendingUp, MapPin } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { toast } from "sonner";
import api from "../lib/api";
import { RECRUITMENT_POSTES } from "../data/content";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-navy px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

function Success({ reference, onReset, label }) {
  return (
    <div data-testid="form-success" className="rounded-3xl border border-gold/25 bg-navy-elevated p-10 text-center">
      <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.3} />
      <h3 className="mt-5 font-serif text-3xl text-white">Candidature transmise</h3>
      <p className="mt-3 text-slate-300">{label}</p>
      <p className="mt-4 inline-block rounded-full border border-gold/40 px-4 py-2 font-mono text-sm text-gold" data-testid="success-reference">Réf. {reference}</p>
      <button onClick={onReset} className="mt-8 block w-full rounded-full border border-white/15 px-6 py-3 text-sm text-white hover:border-gold/50">Nouvelle candidature</button>
    </div>
  );
}

export default function Rejoindre() {
  const [tab, setTab] = useState("recruitment");
  return (
    <div data-testid="rejoindre-page">
      <Seo title="Recrutement & apporteurs d'affaires" path="/rejoindre" description="Rejoignez Speed Détresse : techniciens, commerciaux, apporteurs d'affaires. Déposez votre candidature et votre CV." />
      <PageHero
        overline="Recrutement & partenariats"
        title={["Nous", "rejoindre"]}
        subtitle="Techniciens, commerciaux, apporteurs d'affaires : construisez l'avenir de la gestion de logements avec Speed Détresse."
        testid="rejoindre-hero"
      />

      <section className="relative z-10 mx-auto max-w-4xl px-5 py-14 lg:px-8">
        <div className="mb-10 flex gap-3">
          <button data-testid="tab-recruitment" onClick={() => setTab("recruitment")}
            className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors ${tab === "recruitment" ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
            <Briefcase className="h-4 w-4" /> Recrutement
          </button>
          <button data-testid="tab-partner" onClick={() => setTab("partner")}
            className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors ${tab === "partner" ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
            <Handshake className="h-4 w-4" /> Apporteur d'affaires
          </button>
        </div>
        <Reveal>{tab === "recruitment" ? <RecruitmentForm /> : <PartnerBlock />}</Reveal>
      </section>
    </div>
  );
}

function RecruitmentForm() {
  const empty = { nom: "", email: "", telephone: "", poste: RECRUITMENT_POSTES[0], ville: "", experience: "", disponibilite: "", secteur: "", message: "", consentement: false };
  const [f, setF] = useState(empty);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const hp = e.target.hp?.value || "";
    if (!f.consentement) return toast.error("Merci d'accepter le traitement de vos données.");
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("hp", hp);
      if (file) fd.append("cv", file);
      const r = await api.recruitment(fd);
      setDone(r.reference);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue.");
    } finally { setBusy(false); }
  };

  if (done) return <Success reference={done} onReset={() => { setDone(null); setF(empty); setFile(null); }} label="Votre candidature et votre CV ont été enregistrés." />;

  return (
    <form onSubmit={submit} data-testid="recruitment-form" className="space-y-5 rounded-3xl border border-white/10 bg-navy-surface/60 p-8 md:p-10">
      <input type="text" name="hp" defaultValue="" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div>
        <label className="mb-2 block text-xs uppercase tracking-wide text-slate-400">Poste visé</label>
        <select data-testid="rec-poste" value={f.poste} onChange={(e) => setF({ ...f, poste: e.target.value })} className={inputCls}>
          {RECRUITMENT_POSTES.map((p) => <option key={p} value={p} className="bg-navy">{p}</option>)}
        </select>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input required data-testid="rec-nom" placeholder="Nom / prénom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={inputCls} />
        <input required data-testid="rec-telephone" placeholder="Téléphone" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} className={inputCls} />
        <input required type="email" data-testid="rec-email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
        <input data-testid="rec-ville" placeholder="Ville" value={f.ville} onChange={(e) => setF({ ...f, ville: e.target.value })} className={inputCls} />
        <input data-testid="rec-experience" placeholder="Expérience" value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })} className={inputCls} />
        <input data-testid="rec-disponibilite" placeholder="Disponibilités" value={f.disponibilite} onChange={(e) => setF({ ...f, disponibilite: e.target.value })} className={inputCls} />
      </div>
      <input data-testid="rec-secteur" placeholder="Secteur géographique souhaité" value={f.secteur} onChange={(e) => setF({ ...f, secteur: e.target.value })} className={inputCls} />
      <textarea rows="3" data-testid="rec-message" placeholder="Message / motivation" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={inputCls} />

      <label className="flex cursor-pointer items-center gap-4 rounded-xl border border-dashed border-gold/40 bg-navy p-5 transition-colors hover:border-gold" data-testid="rec-cv-label">
        <Upload className="h-6 w-6 flex-shrink-0 text-gold" strokeWidth={1.5} />
        <div className="min-w-0">
          <p className="text-sm text-white">{file ? file.name : "Déposer votre CV (PDF, DOCX — max 6 Mo)"}</p>
          <p className="text-xs text-slate-500">{file ? `${(file.size / 1024).toFixed(0)} Ko` : "Cliquez pour sélectionner un fichier"}</p>
        </div>
        <input type="file" data-testid="rec-cv-input" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>

      <label className="flex items-start gap-3 text-xs text-slate-400">
        <input type="checkbox" data-testid="rec-consent" checked={f.consentement} onChange={(e) => setF({ ...f, consentement: e.target.checked })} className="mt-0.5 accent-gold" />
        J'accepte que ma candidature et mes documents soient traités par Speed Détresse.
      </label>
      <button type="submit" disabled={busy} data-testid="rec-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Envoyer ma candidature <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
}

function PartnerBlock() {
  const empty = { nom: "", email: "", telephone: "", ville: "", reseau: "", message: "", consentement: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const hp = e.target.hp?.value || "";
    if (!f.consentement) return toast.error("Merci d'accepter le traitement de vos données.");
    setBusy(true);
    try { const r = await api.partner({ ...f, hp }); setDone(r.reference); }
    catch (err) { toast.error("Une erreur est survenue."); } finally { setBusy(false); }
  };

  if (done) return <Success reference={done} onReset={() => { setDone(null); setF(empty); }} label="Votre candidature apporteur d'affaires est devenue un dossier partenaire." />;

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { icon: Users, t: "Un réseau", d: "Recommandez Speed Détresse autour de vous." },
          { icon: TrendingUp, t: "Une rémunération", d: "Chaque dossier abouti vous rapporte." },
          { icon: MapPin, t: "Un territoire", d: "Développez le secteur d'Amiens et au-delà." },
        ].map((c) => (
          <div key={c.t} className="rounded-2xl border border-white/10 bg-navy-surface/60 p-6">
            <c.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
            <h4 className="mt-3 font-serif text-lg text-white">{c.t}</h4>
            <p className="mt-1 text-sm text-slate-400">{c.d}</p>
          </div>
        ))}
      </div>

      <form onSubmit={submit} data-testid="partner-form" className="space-y-5 rounded-3xl border border-white/10 bg-navy-surface/60 p-8 md:p-10">
        <input type="text" name="hp" defaultValue="" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
        <Overline>Devenir apporteur d'affaires</Overline>
        <div className="grid gap-4 sm:grid-cols-2">
          <input required data-testid="partner-nom" placeholder="Nom / prénom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={inputCls} />
          <input required data-testid="partner-telephone" placeholder="Téléphone" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} className={inputCls} />
          <input required type="email" data-testid="partner-email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
          <input data-testid="partner-ville" placeholder="Ville" value={f.ville} onChange={(e) => setF({ ...f, ville: e.target.value })} className={inputCls} />
        </div>
        <input data-testid="partner-reseau" placeholder="Votre réseau / activité" value={f.reseau} onChange={(e) => setF({ ...f, reseau: e.target.value })} className={inputCls} />
        <textarea rows="3" data-testid="partner-message" placeholder="Message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={inputCls} />
        <label className="flex items-start gap-3 text-xs text-slate-400">
          <input type="checkbox" data-testid="partner-consent" checked={f.consentement} onChange={(e) => setF({ ...f, consentement: e.target.checked })} className="mt-0.5 accent-gold" />
          J'accepte que mes données soient traitées pour ma candidature partenaire.
        </label>
        <button type="submit" disabled={busy} data-testid="partner-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Envoyer ma candidature <ArrowRight className="h-4 w-4" /></>}
        </button>
      </form>
    </div>
  );
}
